/**
 * Which of a viewer's consumers a `…screenshare_stopped` event ends.
 *
 * The event arrives over signalling; the consumer for a NEW share arrives over
 * the SFU socket. The two are not ordered against each other, so a stop and a
 * quick re-share could land the new consumer first — and "close every screen
 * consumer from this peer" then closed the share that had just started, and
 * the room saw nothing.
 *
 * The web client now names the producers its share ended (`producerIds`). When
 * any of this viewer's screen consumers carries one of those ids, only those
 * are closed. When none does, the stop is read the old way — every screen
 * consumer from that peer — because a viewer on another SFU node consumes a
 * mirror under a translated id (see docs/signaling-sdk-reference.md), and a
 * stop that matched nothing there would leave the ended share frozen on the
 * stage. So naming the ids can only narrow what a stop closes, never leave a
 * dead share up. Senders that name none (the mobile app, gateways) keep the
 * old reading too.
 */

/** Signaling stamps the bare user id; an SFU peerId may carry a `__<session>` suffix. */
const base = (id: string) => id.split('__')[0]

/**
 * True for shared content, whichever name it arrives under.
 *
 * A browser screen share produces `'screen'`. Content from a room system does
 * not: the mixer tags H.323 H.239 presentations and SIP slides as
 * `'screenshare'`, and that is what the SFU fans out. Both mean "this is the
 * presentation, not a camera", and every routing decision has to treat them
 * alike — matching only `'screen'` sends a room system's shared document down
 * the camera path, where it is appended to that peer's face stream and never
 * appears as a presentation.
 */
export function isScreenSourceType(sourceType: string): boolean {
  return sourceType === 'screen' || sourceType === 'screenshare'
}

/** The producer ids a stop event names, or null when it names none. */
export function stoppedProducerIds(payload: unknown): Set<string> | null {
  const ids = (payload as { producerIds?: unknown } | null)?.producerIds
  if (!Array.isArray(ids)) return null
  return new Set(ids.filter((id): id is string => typeof id === 'string'))
}

type ShareConsumer = { peerId: string; sourceType: string; producerId: string }

/** The consumers a stop from `from` ends, out of `entries`. */
export function consumersEndedByShareStop<E extends ShareConsumer>(
  entries: E[],
  from: string,
  producerIds: Set<string> | null,
): E[] {
  const fromPeer = entries.filter((e) => base(e.peerId) === base(from) && isScreenSourceType(e.sourceType))
  if (producerIds === null) return fromPeer
  const named = fromPeer.filter((e) => producerIds.has(e.producerId))
  return named.length > 0 ? named : fromPeer
}
